import type { ReactNode } from "react";

export const TRAINER_EMOJI: Record<string, string> = {
  "english-sounds": "🔊",
  "word-in-frame": "🎬",
  "phrase-director": "🎭",
};

export function trainerEmoji(slug: string): string {
  return TRAINER_EMOJI[slug] ?? "✨";
}

const TRAINER_ICON_CLASS: Record<string, string> = {
  "english-sounds": "sound",
  "word-in-frame": "frame",
  "phrase-director": "phrase",
};

/** CSS modifier class ("sound" | "frame" | "phrase") for `.icon-badge`, keyed by trainer slug. */
export function trainerIconClass(slug: string): string {
  return TRAINER_ICON_CLASS[slug] ?? "sound";
}

/**
 * Original geometric line icons — no stock art, no emoji glyphs. Each traces the
 * shape of its trainer: a soundwave, a picture frame, a speech bubble mid-line.
 * Colour comes entirely from `currentColor` so `.icon-badge` variants theme them.
 */
export function TrainerIcon({ slug }: { slug: string }) {
  switch (slug) {
    case "english-sounds":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M4 14v-4M8 17v-10M12 19v-14M16 15.5v-7M20 12.5v-1"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      );
    case "word-in-frame":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3.5" y="4.5" width="17" height="15" rx="3" stroke="currentColor" strokeWidth="2" />
          <circle cx="9" cy="10" r="1.6" fill="currentColor" />
          <path
            d="M4.5 17.5l4.2-4.4a1.6 1.6 0 0 1 2.28-.05l2.02 1.94a1.6 1.6 0 0 0 2.2.04l1.6-1.43a1.6 1.6 0 0 1 2.1-.02l1.6 1.42"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "phrase-director":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v6A2.5 2.5 0 0 1 17.5 15H12l-4.2 3.4A.7.7 0 0 1 6.7 18v-3H6.5A2.5 2.5 0 0 1 4 12.5v-6Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <circle cx="8.5" cy="9.5" r="1" fill="currentColor" />
          <circle cx="12" cy="9.5" r="1" fill="currentColor" />
          <circle cx="15.5" cy="9.5" r="1" fill="currentColor" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3l1.8 5.6H19l-4.6 3.4 1.8 5.6L12 14.2 7.8 17.6l1.8-5.6L5 8.6h5.2L12 3z" fill="currentColor" />
        </svg>
      );
  }
}

export function PocketFrame({
  children,
  className = "",
  wide = false,
}: {
  children: ReactNode;
  className?: string;
  wide?: boolean;
}) {
  const extras = [wide ? "wide" : "", className].filter(Boolean).join(" ");
  return (
    <div className={extras ? `ws ${extras}` : "ws"}>
      <div className="desk">
        <div className="stage">
          {children}
        </div>
      </div>
    </div>
  );
}
